import type {
  InvoiceBulkSendResult,
  InvoiceBulkTarget,
  InvoiceSendSkipReason,
} from '@clockwise/clients/invoices';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  FlaskConical,
  Loader2,
  Send,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { invoicesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { runBulkSendInChunks } from './invoice-bulk-selection';

/** Rows listed under a reason before it folds the rest into "+ N more". */
const ROWS_PER_REASON = 5;

/** The "will send" list is for spot-checking, not reading end to end; past
 *  this it says how many more there are rather than render thousands of rows. */
const SEND_ROWS_SHOWN = 200;

/** The order reasons are listed in: the ones a person can fix first. */
const REASON_LABELS: Record<InvoiceSendSkipReason, string> = {
  NO_RECIPIENT: 'No To address',
  NOT_APPROVED: 'Not approved',
  ALREADY_SENDING: 'Already being sent',
  FORBIDDEN: 'No edit access',
  NOT_FOUND: 'Not found',
};

type Skipped = InvoiceBulkSendResult['skipped'];

/** Skipped rows grouped by reason, largest group first. */
export function groupSkipped(skipped: Skipped): Array<{
  reason: InvoiceSendSkipReason;
  rows: Skipped;
}> {
  const byReason = new Map<InvoiceSendSkipReason, Skipped>();
  for (const row of skipped) {
    const list = byReason.get(row.reason);
    if (list) list.push(row);
    else byReason.set(row.reason, [row]);
  }
  return [...byReason]
    .map(([reason, rows]) => ({ reason, rows }))
    .sort((a, b) => b.rows.length - a.rows.length);
}

/**
 * Email the selected invoices, each to its own contacts, in the background.
 *
 * Before anything is queued, the selection runs through the server as a DRY
 * RUN, and the dialog shows what will actually happen — the same layout
 * whatever the size, so nobody has to learn a second one at 300 invoices:
 *
 *   - counts: how many will send (first sends and resends) and how many are
 *     skipped;
 *   - the skipped, grouped by reason, each group expandable — the part that
 *     needs a person;
 *   - the ones that will send, folded away but there to check.
 *
 * The button quotes the dry run's count, not the selection's. There is no
 * "additional addresses" here: one typed address across many customers' invoices
 * is not a thing anyone means; that is done one invoice at a time.
 */
export function InvoiceBulkSendDialog({
  open,
  target,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  target: InvoiceBulkTarget;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const preview = useQuery({
    queryKey: queryKeys.invoices.sendPreview(target),
    queryFn: () => runBulkSendInChunks(target, (t) => invoicesApi.previewBulkSend(t)),
    // Not while the real send is in flight: its invalidation would re-run it.
    enabled: open && !busy,
    placeholderData: keepPreviousData,
    // A dry run describes the rows as they are NOW; never serve a stale one.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const config = useQuery({
    queryKey: queryKeys.invoices.sendConfig(),
    queryFn: () => invoicesApi.sendConfig(),
    enabled: open,
    staleTime: 60_000,
  });
  const redirectTo = config.data?.data.redirectToSender ? config.data.data.redirectTo : null;

  const settled = preview.isSuccess && !preview.isPlaceholderData;
  const result = settled ? preview.data.data : null;
  const willSend = result?.send.length ?? 0;
  const resends = result?.send.filter((r) => r.status === 'sent').length ?? 0;
  const skipped = result?.skipped.length ?? 0;
  const groups = result ? groupSkipped(result.skipped) : [];
  const noun = (n: number) => (n === 1 ? 'invoice' : 'invoices');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-lg" data-testid="invoice-bulk-send-dialog">
        <DialogHeader>
          <DialogTitle>
            {result
              ? `Email ${willSend.toLocaleString()} of ${(willSend + skipped).toLocaleString()} ${noun(willSend + skipped)}?`
              : 'Email the selected invoices?'}
          </DialogTitle>
          <DialogDescription>
            Each invoice goes to its own contacts. Sending runs in the background; you can close
            this page.
          </DialogDescription>
        </DialogHeader>

        {redirectTo ? (
          <div
            role="note"
            className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
          >
            <FlaskConical
              className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400"
              aria-hidden
            />
            <span>
              Test mode: every email goes only to you ({redirectTo}), and no invoice is marked sent.
            </span>
          </div>
        ) : null}

        {preview.isError ? (
          <p role="alert" className="text-destructive text-sm">
            Couldn’t check these invoices, so nothing can be sent yet. Try again.
          </p>
        ) : !result ? (
          <p className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Checking the selected invoices…
          </p>
        ) : (
          // `min-w-0`: DialogContent is a grid, and a grid item will not shrink
          // below its longest row without it — the rows would spill out of the
          // dialog instead of truncating.
          <div className="flex min-h-0 min-w-0 flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <Stat
                label="Will send"
                value={willSend}
                detail={`${(willSend - resends).toLocaleString()} first ${willSend - resends === 1 ? 'send' : 'sends'} · ${resends.toLocaleString()} ${resends === 1 ? 'resend' : 'resends'}`}
              />
              <Stat
                label="Skipped"
                value={skipped}
                tone={skipped > 0 ? 'warning' : undefined}
                detail={
                  skipped === 0
                    ? 'Nothing to fix'
                    : `${groups.length} ${groups.length === 1 ? 'reason' : 'reasons'}`
                }
              />
            </div>

            <div className="flex max-h-[45vh] min-w-0 flex-col gap-3 overflow-y-auto">
              {skipped > 0 ? (
                <section className="flex flex-col gap-1.5" data-testid="bulk-send-skipped">
                  <p className="font-medium text-sm">Skipped</p>
                  <div className="divide-y rounded-md border">
                    {groups.map((group, i) => (
                      <Group
                        key={group.reason}
                        defaultOpen={i === 0}
                        icon={
                          <AlertTriangle
                            className="size-4 text-amber-700 dark:text-amber-400"
                            aria-hidden
                          />
                        }
                        title={REASON_LABELS[group.reason]}
                        count={group.rows.length}
                      >
                        {group.rows.slice(0, ROWS_PER_REASON).map((r) => (
                          <Row
                            key={r.id}
                            number={r.invoiceNumber ?? 'Unknown invoice'}
                            detail={r.customerName ?? ''}
                          />
                        ))}
                        {group.rows.length > ROWS_PER_REASON ? (
                          <p className="px-3 py-1.5 text-muted-foreground text-xs">
                            + {(group.rows.length - ROWS_PER_REASON).toLocaleString()} more
                          </p>
                        ) : null}
                      </Group>
                    ))}
                  </div>
                </section>
              ) : null}

              {willSend > 0 ? (
                <div className="rounded-md border">
                  <Group
                    icon={<Check className="size-4 text-emerald-600" aria-hidden />}
                    title={`Show the ${willSend.toLocaleString()} that will send`}
                  >
                    {result.send.slice(0, SEND_ROWS_SHOWN).map((r) => (
                      <Row
                        key={r.id}
                        number={r.invoiceNumber}
                        detail={[
                          r.customerName,
                          r.to.join(', ') + (r.ccCount > 0 ? ` +${r.ccCount} CC` : ''),
                          r.status === 'sent' ? 'resend' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    ))}
                    {willSend > SEND_ROWS_SHOWN ? (
                      <p className="px-3 py-1.5 text-muted-foreground text-xs">
                        Showing {SEND_ROWS_SHOWN} of {willSend.toLocaleString()}
                      </p>
                    ) : null}
                  </Group>
                </div>
              ) : null}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={busy || !result || willSend === 0} onClick={onConfirm}>
            {busy ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Send className="size-4" aria-hidden />
            )}
            {result ? `Send ${willSend.toLocaleString()}` : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: number;
  detail: string;
  tone?: 'warning';
}) {
  return (
    <div className="rounded-md bg-muted/60 px-3 py-2">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p
        className={
          tone === 'warning'
            ? 'font-medium text-amber-700 text-xl tabular-nums dark:text-amber-400'
            : 'font-medium text-xl tabular-nums'
        }
      >
        {value.toLocaleString()}
      </p>
      <p className="text-muted-foreground text-xs">{detail}</p>
    </div>
  );
}

function Group({
  icon,
  title,
  count,
  defaultOpen = false,
  children,
}: {
  icon: ReactNode;
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
      >
        {open ? (
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
        )}
        {icon}
        <span className="flex-1">{title}</span>
        {count === undefined ? null : (
          <span className="text-muted-foreground tabular-nums">{count.toLocaleString()}</span>
        )}
      </button>
      {open ? <div className="border-t">{children}</div> : null}
    </div>
  );
}

function Row({ number, detail }: { number: string; detail: string }) {
  return (
    <div className="flex gap-3 px-3 py-1.5 text-sm">
      <span className="w-32 shrink-0 tabular-nums">{number}</span>
      <span className="min-w-0 truncate text-muted-foreground" title={detail}>
        {detail}
      </span>
    </div>
  );
}
